import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Bold,
  Italic,
  Underline,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  CheckSquare,
  Quote,
  Code,
  Link as LinkIcon,
  Palette,
  ChevronDown,
  Paperclip,
  X,
  Upload,
  FileIcon,
  Image as ImageIcon,
  Save,
  Check,
  Sparkles,
  Zap,
  Users,
  Clock,
  Download,
} from "lucide-react";
// Office_hub in that css is there from 6143
const NotesSection = () => {
  const [content, setContent] = useState("");
  const [showFloatingToolbar, setShowFloatingToolbar] = useState(false);
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashMenuPosition, setSlashMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [selectedText, setSelectedText] = useState("");
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  const [showTemplates, setShowTemplates] = useState(false);
  const [saveStatus, setSaveStatus] = useState("saved");
  const [attachments, setAttachments] = useState([
    { id: "1", name: "wireframe-v2.fig", size: "2.4 MB", type: "file" },
    { id: "2", name: "client-feedback.pdf", size: "856 KB", type: "document" },
  ]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  const slashCommands = [
    {
      id: "h1",
      label: "Heading 1",
      description: "Large heading",
      icon: Heading1,
      action: "heading1",
    },
    {
      id: "h2",
      label: "Heading 2",
      description: "Medium heading",
      icon: Heading2,
      action: "heading2",
    },
    {
      id: "h3",
      label: "Heading 3",
      description: "Small heading",
      icon: Heading3,
      action: "heading3",
    },
    {
      id: "bullet",
      label: "Bullet List",
      description: "Create a bullet list",
      icon: List,
      action: "bullet",
    },
    {
      id: "number",
      label: "Numbered List",
      description: "Create a numbered list",
      icon: ListOrdered,
      action: "numbered",
    },
    {
      id: "check",
      label: "Checklist",
      description: "Create a checklist",
      icon: CheckSquare,
      action: "checklist",
    },
    {
      id: "quote",
      label: "Quote",
      description: "Add a blockquote",
      icon: Quote,
      action: "quote",
    },
    {
      id: "code",
      label: "Code Block",
      description: "Add a code block",
      icon: Code,
      action: "code",
    },
  ];

  const templates = [
    {
      id: "meeting",
      name: "Meeting Notes",
      icon: "📝",
      content: `# Meeting Notes
Date: ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}

## Attendees
• 

## Agenda
1. 

## Discussion Points
• 

## Action Items
☐ 
☐ 

## Next Steps
`,
    },
    {
      id: "project",
      name: "Project Brief",
      icon: "🎯",
      content: `# Project Brief

## Overview
Brief description of the project...

## Objectives
• 
• 

## Timeline
Start Date: 
End Date: 

## Key Stakeholders
• 

## Success Metrics
1. 

## Risks & Challenges
• 
`,
    },
    {
      id: "task",
      name: "Task Notes",
      icon: "✅",
      content: `# Task Notes

## Context
What needs to be done and why...

## Requirements
☐ 
☐ 

## Resources
• 

## Notes
`,
    },
    {
      id: "standup",
      name: "Daily Standup",
      icon: "🌅",
      content: `# Daily Standup - ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" })}

## ✅ Yesterday
• 

## 🎯 Today
• 

## 🚧 Blockers
• None

## 💡 Notes
`,
    },
  ];

  useEffect(() => {
    if (content) {
      setSaveStatus("saving");
      const timer = setTimeout(() => {
        setSaveStatus("saved");
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [content]);

  const wordCount = content.trim().split(/\s+/).filter(Boolean).length;

  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      setSelectedText(selection.toString());
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      setToolbarPosition({
        top: rect.top - 60,
        left: rect.left + rect.width / 2 - 150,
      });
      setShowFloatingToolbar(true);
    } else {
      setShowFloatingToolbar(false);
    }
  };

  const applyFormat = (format) => {
    const formats = {
      bold: "**",
      italic: "_",
      underline: "__",
      code: "`",
    };

    if (selectedText && formats[format]) {
      const wrapper = formats[format];
      const newContent = content.replace(
        selectedText,
        `${wrapper}${selectedText}${wrapper}`,
      );
      setContent(newContent);
      setShowFloatingToolbar(false);
    }
  };

  const insertSlashCommand = (action) => {
    const insertTexts = {
      heading1: "\n# Heading 1\n",
      heading2: "\n## Heading 2\n",
      heading3: "\n### Heading 3\n",
      bullet: "\n• Item\n",
      numbered: "\n1. Item\n",
      checklist: "\n☐ Task item\n",
      quote: "\n> Quote text\n",
      code: "\n```\nCode block\n```\n",
    };

    if (insertTexts[action]) {
      setContent(content + insertTexts[action]);
    }
    setShowSlashMenu(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "/" && !showSlashMenu) {
      setShowSlashMenu(true);

      setTimeout(() => {
        const textarea = editorRef.current;
        if (textarea) {
          const { selectionStart } = textarea;
          const textBeforeCursor = textarea.value.substring(0, selectionStart);
          const lines = textBeforeCursor.split("\n");
          const currentLine = lines.length;

          const lineHeight = 24;
          const topOffset = currentLine * lineHeight;

          setSlashMenuPosition({
            top: topOffset + 60,
            left: 20,
          });
        }
      }, 0);
    } else if (e.key === "Escape") {
      setShowSlashMenu(false);
    }
  };

  const applyTemplate = (templateContent) => {
    setContent(templateContent);
    setShowTemplates(false);
    setSaveStatus("unsaved");
  };

  const handleFileUpload = (files) => {
    if (!files) return;

    setIsUploading(true);
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);

          Array.from(files).forEach((file) => {
            const newAttachment = {
              id: Date.now().toString() + Math.random(),
              name: file.name,
              size: (file.size / 1024).toFixed(0) + " KB",
              type: file.type.startsWith("image/") ? "image" : "document",
            };
            setAttachments((prev) => [...prev, newAttachment]);
          });

          return 0;
        }
        return prev + 10;
      });
    }, 100);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const removeAttachment = (id) => {
    setAttachments(attachments.filter((a) => a.id !== id));
  };

  return (
    <section className="notes-section">
      {/* Background Elements */}
      {/* <div className="notes-section__background">
        <div className="notes-section__blob notes-section__blob--1"></div>
        <div className="notes-section__blob notes-section__blob--2"></div>
      </div> */}

      <div className="notes-section__container">
        {/* Header */}
        <div className="notes-section__header">
          <div className="notes-section__badge">
            <FileText className="notes-section__badge-icon" />
            <span className="notes-section__badge-text">Rich Text Notes</span>
          </div>

          <h2 className="notes-section__title">Notes That Work With You</h2>
          <p className="notes-section__subtitle">
            Notion-style rich text editor with slash commands, templates, and
            attachments.
            <br />
            Everything auto-saves. Nothing gets lost.
          </p>
        </div>

        {/* Main Editor Area */}
        <div className="notes-section__grid">
          {/* Left: Editor */}
          <div className="notes-section__editor-col">
            <div className="notes-section__editor-card">
              {/* Editor Header */}
              <div className="notes-section__editor-header">
                <div className="notes-section__editor-header-content">
                  <div className="notes-section__editor-title-wrapper">
                    <div className="notes-section__editor-icon">
                      <FileText />
                    </div>
                    <div>
                      <h3 className="notes-section__editor-title">
                        Task: Website Redesign
                      </h3>
                      <p className="notes-section__editor-meta">
                        Auto-created from TaskMaster • Alpha Corp
                      </p>
                    </div>
                  </div>
                  <div className="notes-section__editor-actions">
                    <div className="notes-section__template-wrapper">
                      <button
                        onClick={() => setShowTemplates(!showTemplates)}
                        className="notes-section__template-btn"
                      >
                        <Sparkles />
                        <span>Templates</span>
                        <ChevronDown />
                      </button>

                      {/* Templates Dropdown */}
                      <AnimatePresence>
                        {showTemplates && (
                          <motion.div
                            initial={{ opacity: 0, y: -10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10, scale: 0.95 }}
                            className="notes-section__template-dropdown"
                          >
                            {templates.map((template, index) => (
                              <motion.button
                                key={template.id}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.05 }}
                                onClick={() => applyTemplate(template.content)}
                                className="notes-section__template-item"
                              >
                                <div className="notes-section__template-icon">
                                  {template.icon}
                                </div>
                                <div className="notes-section__template-info">
                                  <div className="notes-section__template-name">
                                    {template.name}
                                  </div>
                                  <div className="notes-section__template-preview">
                                    {template.content
                                      .split("\n")[0]
                                      .replace("#", "")
                                      .trim()}
                                  </div>
                                </div>
                              </motion.button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </div>

              {/* Editor Content */}
              <div
                className={`notes-section__editor-content ${isDragging ? "notes-section__editor-content--dragging" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {/* Drag Overlay */}
                <AnimatePresence>
                  {isDragging && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="notes-section__drag-overlay"
                    >
                      <div className="notes-section__drag-content">
                        <Upload className="notes-section__drag-icon" />
                        <p className="notes-section__drag-title">
                          Drop files to attach
                        </p>
                        <p className="notes-section__drag-subtitle">
                          Images, documents, any file type
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <textarea
                  ref={editorRef}
                  value={content}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setContent(newValue);

                    if (showSlashMenu) {
                      const textarea = e.target;
                      const cursorPos = textarea.selectionStart;
                      const charBeforeCursor = newValue.charAt(cursorPos - 1);
                      const charAtCursor = newValue.charAt(cursorPos);

                      if (charBeforeCursor !== "/" && charAtCursor !== "/") {
                        const textNearCursor = newValue.substring(
                          Math.max(0, cursorPos - 20),
                          cursorPos,
                        );
                        if (
                          !textNearCursor.includes("/") ||
                          textNearCursor.lastIndexOf("/") <
                            textNearCursor.length - 10
                        ) {
                          setShowSlashMenu(false);
                        }
                      }
                    }
                  }}
                  onSelect={handleTextSelection}
                  onKeyDown={handleKeyDown}
                  placeholder="Start writing... or type '/' for commands"
                  className="notes-section__textarea"
                />

                {/* Floating Toolbar */}
                <AnimatePresence>
                  {showFloatingToolbar && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.9 }}
                      style={{
                        position: "fixed",
                        top: toolbarPosition.top,
                        left: toolbarPosition.left,
                        zIndex: 1000,
                      }}
                      className="notes-section__floating-toolbar"
                    >
                      {[
                        { icon: Bold, action: "bold", label: "Bold" },
                        { icon: Italic, action: "italic", label: "Italic" },
                        {
                          icon: Underline,
                          action: "underline",
                          label: "Underline",
                        },
                        { icon: Code, action: "code", label: "Code" },
                        { icon: LinkIcon, action: "link", label: "Link" },
                        { icon: Palette, action: "color", label: "Color" },
                      ].map((tool, index) => (
                        <motion.button
                          key={tool.action}
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.03 }}
                          onClick={() => applyFormat(tool.action)}
                          className="notes-section__toolbar-btn"
                          title={tool.label}
                        >
                          <tool.icon />
                        </motion.button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Slash Commands Menu */}
                <AnimatePresence>
                  {showSlashMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      className="notes-section__slash-menu"
                      style={{
                        top: slashMenuPosition.top,
                        left: slashMenuPosition.left,
                      }}
                    >
                      <div className="notes-section__slash-header">
                        <p>BASIC BLOCKS</p>
                      </div>
                      {slashCommands.map((command, index) => (
                        <motion.button
                          key={command.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.03 }}
                          onClick={() => insertSlashCommand(command.action)}
                          className="notes-section__slash-item"
                        >
                          <div className="notes-section__slash-icon">
                            <command.icon />
                          </div>
                          <div className="notes-section__slash-info">
                            <div className="notes-section__slash-label">
                              {command.label}
                            </div>
                            <div className="notes-section__slash-desc">
                              {command.description}
                            </div>
                          </div>
                        </motion.button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Hint Text */}
                {!content && (
                  <div className="notes-section__hint">
                    <p>
                      <Zap />
                      Press <kbd>Tab</kbd> for AI assist
                    </p>
                  </div>
                )}
              </div>

              {/* Editor Footer */}
              <div className="notes-section__editor-footer">
                <div className="notes-section__footer-left">
                  <div
                    className={`notes-section__save-status notes-section__save-status--${saveStatus}`}
                  >
                    {saveStatus === "saved" ? (
                      <>
                        <Check />
                        <span>Auto-saved</span>
                      </>
                    ) : saveStatus === "saving" ? (
                      <>
                        <Save />
                        <span>Saving...</span>
                      </>
                    ) : null}
                  </div>
                  <div className="notes-section__word-count">
                    <span>{wordCount} words</span>
                  </div>
                  <div className="notes-section__file-count">
                    <Paperclip />
                    <span>{attachments.length} files</span>
                  </div>
                </div>

                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="notes-section__attach-btn"
                >
                  <Paperclip />
                  <span>Attach</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="notes-section__file-input"
                />

                {/* Upload Progress */}
                <AnimatePresence>
                  {isUploading && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="notes-section__upload-progress"
                    >
                      <div className="notes-section__progress-content">
                        <div className="notes-section__progress-header">
                          <Upload />
                          <span>Uploading...</span>
                          <span className="notes-section__progress-percent">
                            {uploadProgress}%
                          </span>
                        </div>
                        <div className="notes-section__progress-bar">
                          <motion.div
                            className="notes-section__progress-fill"
                            initial={{ width: 0 }}
                            animate={{ width: `${uploadProgress}%` }}
                            transition={{ duration: 0.3 }}
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Right: Attachments & Features */}
          <div className="notes-section__sidebar">
            {/* Attachments */}
            <div className="notes-section__attachments-card">
              <div className="notes-section__attachments-header">
                <h4>
                  <Paperclip />
                  Attachments
                </h4>
                <span className="notes-section__attachments-count">
                  {attachments.length}
                </span>
              </div>

              <div className="notes-section__attachments-list">
                <AnimatePresence mode="popLayout">
                  {attachments.map((attachment, index) => (
                    <motion.div
                      key={attachment.id}
                      layout
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20, scale: 0.8 }}
                      transition={{ delay: index * 0.05 }}
                      className="notes-section__attachment-item"
                    >
                      <div className="notes-section__attachment-icon">
                        {attachment.type === "image" ? (
                          <ImageIcon />
                        ) : (
                          <FileIcon />
                        )}
                      </div>
                      <div className="notes-section__attachment-info">
                        <p className="notes-section__attachment-name">
                          {attachment.name}
                        </p>
                        <p className="notes-section__attachment-size">
                          {attachment.size}
                        </p>
                      </div>
                      <div className="notes-section__attachment-actions">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="notes-section__attachment-btn"
                        >
                          <Download />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => removeAttachment(attachment.id)}
                          className="notes-section__attachment-btn notes-section__attachment-btn--delete"
                        >
                          <X />
                        </motion.button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {attachments.length === 0 && (
                  <div className="notes-section__attachments-empty">
                    <Paperclip />
                    <p>No attachments yet</p>
                  </div>
                )}
              </div>

              <button
                onClick={() => fileInputRef.current?.click()}
                className="notes-section__upload-btn"
              >
                <Upload />
                <span>Upload Files</span>
              </button>
            </div>

            {/* Features List */}
            <div className="notes-section__features-card">
              <h4 className="notes-section__features-title">
                <Sparkles />
                Features
              </h4>
              <div className="notes-section__features-list">
                {[
                  { icon: Zap, text: "Slash commands" },
                  { icon: Sparkles, text: "Quick templates" },
                  { icon: Paperclip, text: "File attachments" },
                  { icon: Save, text: "Auto-save" },
                  { icon: Bold, text: "Rich formatting" },
                  { icon: Users, text: "Collaboration" },
                ].map((feature, index) => (
                  <div key={index} className="notes-section__feature-item">
                    <div className="notes-section__feature-icon">
                      <feature.icon />
                    </div>
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NotesSection;
